import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Input,
  Select,
  Tag,
  Spin,
  Empty,
  Popconfirm,
} from "antd";
import {
  SettingOutlined,
  ReloadOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import BillService from "../services/Billservice";
import { notify } from "../components/Customs/NotifyProvider";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const { Option } = Select;
const MODULE_KEY = "general_settings";

// Friendly labels/units for the known settings — everything else (any
// custom key someone adds) just falls back to showing the raw key as-is.
const SETTING_META = {
  cgst_tax: { label: "CGST Tax", group: "Tax", suffix: "%" },
  sgst_tax: { label: "SGST Tax", group: "Tax", suffix: "%" },
  tax_type: { label: "Tax Type", group: "Tax" },
  parcel_charge: { label: "Parcel Charge", group: "Charges", prefix: "₹" },
  ac_charge: { label: "AC Charge", group: "Charges", prefix: "₹" },
  additional_charge: { label: "Additional Charge", group: "Charges", prefix: "₹" },
  drink_cgst_tax: { label: "Drink CGST Tax", group: "Drinks", suffix: "%" },
  drink_sgst_tax: { label: "Drink SGST Tax", group: "Drinks", suffix: "%" },
  drink_vat: { label: "Drink VAT", group: "Drinks", suffix: "%" },
};

const GROUP_COLORS = {
  Tax: "blue",
  Charges: "gold",
  Drinks: "purple",
  Custom: "default",
};

// Hidden from this page for now (not deleted — billing calculations still
// read these 3 keys as-is, this just keeps them off the settings list).
// Un-comment to show them again.
const HIDDEN_KEYS = ["drink_cgst_tax", "drink_sgst_tax", "drink_vat", "additional_charge"];

const GeneralSettings = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const canView = canViewModule(MODULE_KEY);
  const canCreate = canCreateModule(MODULE_KEY);
  const canUpdate = canUpdateModule(MODULE_KEY);
  const canDelete = canDeleteModule(MODULE_KEY);

  const loadSettings = async () => {
    if (!canView) return;

    try {
      setLoading(true);

      const rows = await BillService.getGeneralSettingsList();

      const visible = (rows || []).filter(
        (row) => !HIDDEN_KEYS.includes(row.setting_key)
      );

      const groupOrder = ["Tax", "Charges", "Drinks", "Custom"];

      const sorted = [...visible].sort((a, b) => {
        const groupA = SETTING_META[a.setting_key]?.group || "Custom";
        const groupB = SETTING_META[b.setting_key]?.group || "Custom";
        return groupOrder.indexOf(groupA) - groupOrder.indexOf(groupB);
      });

      setData(
        sorted.map((row) => ({
          ...row,
          isNew: false,
        }))
      );
    } catch (error) {
      notify.error(error?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleAddRow = () => {
    if (!canCreate) {
      notify.error("Permission Denied", "No permission to add setting");
      return;
    }

    setData((prev) => [
      ...prev,
      {
        id: `new_${Date.now()}`,
        setting_key: "",
        setting_value: "",
        isNew: true,
      },
    ]);
  };

  const handleChange = (index, field, value) => {
    setData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleSave = async (row) => {
    if (row.isNew && !canCreate) {
      notify.error("Permission Denied", "No permission to create setting");
      return;
    }

    if (!row.isNew && !canUpdate) {
      notify.error("Permission Denied", "No permission to update setting");
      return;
    }

    try {
      if (!row.setting_key?.trim()) {
        notify.error("Key is required");
        return;
      }

      if (!String(row.setting_value ?? "").trim()) {
        notify.error("Value is required");
        return;
      }

      if (
        row.setting_key === "tax_type" &&
        !["percentage", "value"].includes(String(row.setting_value).trim())
      ) {
        notify.error("tax_type must be percentage or value");
        return;
      }

      setSavingId(row.id);

      if (row.isNew) {
        await BillService.createGeneralSetting({
          setting_key: row.setting_key.trim(),
          setting_value: String(row.setting_value).trim(),
        });
        notify.success("Setting added");
      } else {
        await BillService.updateGeneralSetting({
          id: row.id,
          setting_value: String(row.setting_value).trim(),
        });
        notify.success("Setting updated");
      }

      await loadSettings();
    } catch (error) {
      notify.error(error?.message || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      notify.error("Permission Denied", "No permission to delete setting");
      return;
    }

    try {
      if (row.isNew) {
        setData((prev) => prev.filter((r) => r.id !== row.id));
        notify.success("Row removed");
        return;
      }

      await BillService.deleteGeneralSetting(row.id);
      notify.success("Deleted successfully");
      await loadSettings();
    } catch (error) {
      notify.error(error?.message || "Delete failed");
    }
  };

  const renderValueField = (text, record, index) => {
    const currentKey = String(record.setting_key || "").trim().toLowerCase();
    const disabled = record.isNew ? !canCreate : !canUpdate;
    const meta = SETTING_META[currentKey];

    if (currentKey === "tax_type") {
      return (
        <Select
          value={text || undefined}
          placeholder="Select tax type"
          className="page-select w-full"
          disabled={disabled}
          onChange={(value) => handleChange(index, "setting_value", value)}
        >
          <Option value="percentage">percentage</Option>
          <Option value="value">value</Option>
        </Select>
      );
    }

    return (
      <Input
        value={text}
        placeholder="Enter value"
        disabled={disabled}
        prefix={meta?.prefix}
        suffix={meta?.suffix}
        onChange={(e) => handleChange(index, "setting_value", e.target.value)}
      />
    );
  };

  const columns = [
    {
      title: "Setting",
      dataIndex: "setting_key",
      key: "setting_key",
      render: (text, record, index) => {
        const currentKey = String(text || "").trim().toLowerCase();
        const meta = SETTING_META[currentKey];

        if (!record.isNew) {
          return (
            <div>
              <div className="permission-module-title">
                {meta?.label || text}
              </div>
              <Tag color={GROUP_COLORS[meta?.group] || GROUP_COLORS.Custom}>
                {meta?.group || "Custom"}
              </Tag>
            </div>
          );
        }

        return (
          <Input
            value={text}
            placeholder="Enter key, e.g. custom_charge"
            disabled={!canCreate}
            onChange={(e) =>
              handleChange(index, "setting_key", e.target.value)
            }
          />
        );
      },
    },
    {
      title: "Value",
      dataIndex: "setting_value",
      key: "setting_value",
      render: (text, record, index) => renderValueField(text, record, index),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            className="primary-btn"
            onClick={() => handleSave(record)}
            loading={savingId === record.id}
            disabled={record.isNew ? !canCreate : !canUpdate}
          />
          
           {/* ❗ Delete ONLY for NEW rows */}
          {record.isNew && (
            <Popconfirm
              title="Delete setting"
              description="Are you sure you want to delete this setting?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDelete(record)}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                className="secondary-btn"
              />
            </Popconfirm>
          )}

          
        </div>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view settings" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <SettingOutlined />
          </div>

          <div>
            <h2 className="user-page-title">General Settings</h2>
            <p className="user-page-subtitle">
              Manage system configuration values.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            icon={<ReloadOutlined />}
            className="secondary-btn"
            onClick={loadSettings}
            loading={loading}
            disabled={!canView}
          >
            Refresh
          </Button>

          {/* Custom settings aren't read by any billing calculation — only
              the 9 known keys seeded per restaurant are wired up. Hidden to
              avoid implying a custom entry here would affect bills. */}
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            className="primary-btn"
            onClick={handleAddRow}
            disabled={!canCreate}
          >
            Add Setting
          </Button> */}
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Settings List</h3>
            <p>Edit values or add new settings</p>
          </div>
        </div>

        {loading ? (
          <div className="page-empty">
            <Spin size="large" />
          </div>
        ) : data.length === 0 ? (
          <div className="page-empty">
            <Empty description="No settings found" />
          </div>
        ) : (
          <div className="common-table-wrap">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={data}
              pagination={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSettings;